import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { downloadResumePDF } from "./ResumePDF";
import ResumePreviewATS from "./ResumePreviewATS";
import ResumePreviewProfessional from "./ResumePreviewProfessional";
import ResumePreviewModern from "./ResumePreviewModern";
import {
  Building2, Briefcase, Zap, Download, FileText,
  Check, Plus, Trash2, ChevronDown, ChevronUp,
  AlertTriangle, X,
} from "lucide-react";
import { detectPlaceholders, setAtPath } from "../../lib/placeholders";

// ── Helpers ───────────────────────────────────────────────────────────────────

const GUIDANCE = [
  { id: "ats",          label: "ATS Safe",      Icon: Building2, type: "Large corporation, bank or government",  examples: "RBC · Amazon · Federal Govt",    selectedBg: "bg-[#111]",      iconBg: "bg-[#F3F4F6]", iconColor: "text-[#111]",     recommendation: "ATS Safe template"      },
  { id: "professional", label: "Professional",   Icon: Briefcase, type: "Consulting, finance or professional firm", examples: "McKinsey · KPMG · Deloitte", selectedBg: "bg-[#1D4ED8]",   iconBg: "bg-[#EFF6FF]", iconColor: "text-[#1D4ED8]",  recommendation: "Professional template"  },
  { id: "modern",       label: "Modern",         Icon: Zap,       type: "Tech company, startup or creative",    examples: "Shopify · Google · Figma",       selectedBg: "bg-[#0EA5E9]",   iconBg: "bg-[#F0F9FF]", iconColor: "text-[#0EA5E9]",  recommendation: "Modern template"        },
];

const PREVIEW_COMPONENTS = { ats: ResumePreviewATS, professional: ResumePreviewProfessional, modern: ResumePreviewModern };

// Detect AI placeholder like [X%] or [number] — needs candidate input
const hasPlaceholder = (val) => typeof val === "string" && /\[.+?\]/.test(val);

const fieldClass = (val) =>
  `w-full border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 resize-none ${
    hasPlaceholder(val)
      ? "border-[#F59E0B] bg-[#FFFBEB] focus:ring-[#F59E0B]"
      : "border-border focus:ring-[#0025D8]"
  }`;

function SectionHeader({ title, children }) {
  return (
    <div className="mb-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center justify-between">
        {title}
        {children}
      </div>
    </div>
  );
}

function PlaceholderHint({ value }) {
  if (!hasPlaceholder(value)) return null;
  return <span className="text-[9px] font-mono text-[#F59E0B] ml-1">· fill in [placeholder]</span>;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ResumeEditor({
  initialData,
  analysisId,
  changesMade = [],
  candidateName,
  initialTemplate = null,
  onTemplateChange,
  lockTemplate = null,     // force one template + hide the picker (e.g. "ats" for auto-apply)
  saveEndpoint = null,     // save target override (e.g. an application draft) — else the analysis
}) {
  const [data, setData]             = useState(() => JSON.parse(JSON.stringify(initialData || {})));
  const [template, setTemplate]     = useState(lockTemplate || initialTemplate || "ats");
  const [saveStatus, setSaveStatus] = useState("saved"); // "saved" | "saving" | "unsaved" | "error"
  const [downloading, setDownloading]         = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [expandedJobs, setExpandedJobs]       = useState(() => new Set([0]));
  const [downloadGuard, setDownloadGuard]     = useState(null);
  const saveTimer = useRef(null);

  const PreviewComponent = PREVIEW_COMPONENTS[template] || ResumePreviewATS;

  // Count CONSIDER items
  const considerCount = changesMade.filter(
    (c) => typeof c === "object" && c.confidence === "CONSIDER"
  ).length;

  // ── Auto-save ──────────────────────────────────────────────────────────────
  const scheduleSave = useCallback((updated) => {
    setSaveStatus("unsaved");
    clearTimeout(saveTimer.current);
    const ep = saveEndpoint || (analysisId ? `/resume/analyses/${analysisId}/structured` : null);
    if (!ep) return;
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await api.patch(ep, { resume_structured: updated });
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      }
    }, 800);
  }, [analysisId, saveEndpoint]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // ── Updaters ───────────────────────────────────────────────────────────────
  const update = (updater) => {
    setData((prev) => {
      const next = updater(JSON.parse(JSON.stringify(prev)));
      scheduleSave(next);
      return next;
    });
  };

  const setContact   = (field, val)         => update((d) => { d.contact = { ...d.contact, [field]: val }; return d; });
  const setSummary   = (val)                => update((d) => { d.summary = val; return d; });
  const setExpField  = (i, field, val)      => update((d) => { d.experience[i][field] = val; return d; });
  const setExpBullet = (i, j, val)          => update((d) => { d.experience[i].bullets[j] = val; return d; });
  const addBullet    = (i)                  => update((d) => { d.experience[i].bullets.push(""); return d; });
  const removeBullet = (i, j)               => update((d) => { d.experience[i].bullets.splice(j, 1); return d; });
  const addExp       = ()                   => update((d) => { d.experience = d.experience || []; d.experience.push({ title: "", company: "", location: "", start_date: "", end_date: "", bullets: [""] }); setExpandedJobs((s) => new Set([...s, (d.experience || []).length])); return d; });
  const removeExp    = (i)                  => update((d) => { d.experience.splice(i, 1); return d; });
  const setEduField  = (i, field, val)      => update((d) => { d.education[i][field] = val; return d; });
  const setSkillName = (i, val)             => update((d) => { d.skills[i].name = val; return d; });
  const setSkillItems= (i, val)             => update((d) => { d.skills[i].items = val.split(",").map((s) => s.trim()).filter(Boolean); return d; });
  const addSkill     = ()                   => update((d) => { d.skills = d.skills || []; d.skills.push({ name: "", items: [] }); return d; });
  const removeSkill  = (i)                  => update((d) => { d.skills.splice(i, 1); return d; });
  const setCertField = (i, field, val)      => update((d) => { d.certifications[i][field] = val; return d; });

  // ── Downloads ──────────────────────────────────────────────────────────────
  const doDownloadPDF = async () => {
    setDownloading(true);
    try { await downloadResumePDF(data, template, data?.contact?.name || candidateName); }
    catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };

  const doDownloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      const res = await api.post("/resume/export/docx",
        { resume_structured: data, name: data?.contact?.name || candidateName || "Resume", template },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href     = url;
      a.download = `${(data?.contact?.name || "resume").replace(/\s+/g, "_")}_resume.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloadingDocx(false); }
  };

  const handlePDF = () => {
    if (detectPlaceholders(data).length > 0) { setDownloadGuard("pdf"); return; }
    doDownloadPDF();
  };

  const handleDocx = () => {
    if (detectPlaceholders(data).length > 0) { setDownloadGuard("docx"); return; }
    doDownloadDocx();
  };

  const handlePlaceholderFill = (path, newValue) => {
    update((d) => { setAtPath(d, path, newValue); return d; });
  };

  const handleTemplateSelect = (id) => {
    setTemplate(id);
    onTemplateChange?.(id);
  };

  const c = data?.contact || {};

  return (
    <div className="space-y-5">

      {/* ── Template guidance (hidden when a template is locked, e.g. ATS-only for auto-apply) ── */}
      {!lockTemplate && (
      <div className="border border-[#0025D8]/25 bg-gradient-to-br from-[#EFF6FF] to-[#F8FAFF] overflow-hidden">
        <div className="bg-[#0025D8] px-5 py-3 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-white/60" />
          <p className="text-xs font-bold text-white tracking-wide">Which type of company are you applying to?</p>
        </div>
        <div className="grid grid-cols-3 gap-0 divide-x divide-[#0025D8]/10">
          {GUIDANCE.map((g) => {
            const isSel = template === g.id;
            return (
              <button key={g.id} onClick={() => handleTemplateSelect(g.id)}
                className={`relative text-left p-5 transition-all duration-200 focus:outline-none ${isSel ? `${g.selectedBg} text-white` : "bg-white hover:bg-[#F0F7FF]"}`}>
                {isSel && <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${isSel ? "bg-white/10" : g.iconBg}`}>
                  <g.Icon className={`w-5 h-5 ${isSel ? "text-white" : g.iconColor}`} />
                </div>
                <p className={`font-semibold text-sm leading-snug mb-1.5 ${isSel ? "text-white" : "text-[#111]"}`}>{g.type}</p>
                <p className={`text-[10px] mb-3 ${isSel ? "text-white/70" : "text-muted-foreground"}`}>{g.examples}</p>
                <div className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm ${isSel ? "bg-white/20 text-white" : "bg-[#0025D8]/8 text-[#0025D8]"}`}>
                  → {g.recommendation}
                </div>
              </button>
            );
          })}
        </div>
        {!template && (
          <div className="px-5 py-2 bg-[#0025D8]/5 border-t border-[#0025D8]/10">
            <p className="text-[10px] text-[#0025D8]/70 font-mono">Click your answer above — we'll select the right template automatically</p>
          </div>
        )}
      </div>
      )}

      {/* ── CONSIDER notice ──────────────────────────────────────────── */}
      {considerCount > 0 && (
        <div className="flex items-start gap-3 border border-[#F59E0B]/40 bg-[#FFFBEB] px-4 py-3">
          <div className="w-2 h-2 rounded-full bg-[#F59E0B] flex-shrink-0 mt-1.5" />
          <p className="text-xs text-[#92400E]">
            <span className="font-bold">{considerCount} item{considerCount !== 1 ? "s" : ""} need your input</span>
            {" "}— fields with an amber border contain placeholders like <code className="font-mono bg-[#F59E0B]/20 px-1">[X%]</code> that only you can fill in. Edit them in the form below before downloading.
          </p>
        </div>
      )}

      {/* ── Side-by-side editor + preview ────────────────────────────── */}
      <div className="grid grid-cols-5 gap-4 min-h-[600px]">

        {/* ── Left: Edit form ──────────────────────────────────────── */}
        <div className="col-span-2 border border-border overflow-y-auto max-h-[700px] bg-white">
          <div className="sticky top-0 bg-white border-b border-border px-4 py-2.5 flex items-center justify-between z-10">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Edit Resume</span>
            <span className={`font-mono text-[9px] transition-colors ${
              saveStatus === "saved"   ? "text-[#10B981]" :
              saveStatus === "saving"  ? "text-[#0025D8]" :
              saveStatus === "error"   ? "text-[#E11D48]" :
                                        "text-[#F59E0B]"
            }`}>
              {saveStatus === "saved" ? "✓ Saved" : saveStatus === "saving" ? "Saving…" : saveStatus === "error" ? "Save failed" : "Unsaved changes"}
            </span>
          </div>

          <div className="p-4 space-y-5">

            {/* Contact */}
            <div>
              <SectionHeader title="Contact" />
              {[
                ["name", "Full Name", "text"],
                ["email", "Email", "text"],
                ["phone", "Phone", "text"],
                ["location", "Location", "text"],
                ["linkedin", "LinkedIn URL", "text"],
                ["website", "Website (optional)", "text"],
              ].map(([field, label]) => (
                <div key={field} className="mb-2">
                  <label className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider flex items-center">
                    {label}<PlaceholderHint value={c[field]} />
                  </label>
                  <input
                    className={fieldClass(c[field] || "")}
                    value={c[field] || ""}
                    onChange={(e) => setContact(field, e.target.value)}
                  />
                </div>
              ))}
            </div>

            {/* Summary */}
            <div>
              <SectionHeader title="Professional Summary" />
              <PlaceholderHint value={data.summary} />
              <textarea
                className={`${fieldClass(data.summary || "")} min-h-[80px]`}
                value={data.summary || ""}
                onChange={(e) => setSummary(e.target.value)}
              />
            </div>

            {/* Experience */}
            <div>
              <SectionHeader title="Work Experience">
                <button onClick={addExp} className="flex items-center gap-1 text-[#0025D8] font-mono text-[9px] hover:underline">
                  <Plus className="w-3 h-3" /> Add role
                </button>
              </SectionHeader>
              {(data.experience || []).map((exp, i) => (
                <div key={i} className="border border-border mb-3">
                  <button
                    onClick={() => setExpandedJobs((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary/30"
                  >
                    <span className="text-xs font-semibold truncate">{exp.title || exp.company || `Role ${i + 1}`}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {(exp.bullets || []).some(hasPlaceholder) && <span className="w-2 h-2 rounded-full bg-[#F59E0B]" title="Contains placeholders" />}
                      {expandedJobs.has(i) ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                  </button>

                  {expandedJobs.has(i) && (
                    <div className="px-3 pb-3 space-y-2 border-t border-border pt-2">
                      {[["title","Job Title"],["company","Company"],["location","Location"],["start_date","Start Date"],["end_date","End Date"]].map(([f, lbl]) => (
                        <div key={f}>
                          <label className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider flex items-center">
                            {lbl}<PlaceholderHint value={exp[f]} />
                          </label>
                          <input className={fieldClass(exp[f] || "")} value={exp[f] || ""} onChange={(e) => setExpField(i, f, e.target.value)} />
                        </div>
                      ))}
                      <div>
                        <label className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center justify-between">
                          Bullets
                          <button onClick={() => addBullet(i)} className="flex items-center gap-0.5 text-[#0025D8] hover:underline text-[9px]"><Plus className="w-2.5 h-2.5" /> Add</button>
                        </label>
                        {(exp.bullets || []).map((b, j) => (
                          <div key={j} className="flex gap-1 mb-1 items-start">
                            <textarea
                              className={`${fieldClass(b)} flex-1 min-h-[42px] text-xs`}
                              value={b}
                              onChange={(e) => setExpBullet(i, j, e.target.value)}
                            />
                            <button onClick={() => removeBullet(i, j)} className="mt-1 text-muted-foreground hover:text-[#E11D48] flex-shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => removeExp(i)} className="flex items-center gap-1 text-[9px] font-mono text-[#E11D48] hover:underline mt-1">
                        <Trash2 className="w-3 h-3" /> Remove this role
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Education */}
            <div>
              <SectionHeader title="Education" />
              {(data.education || []).map((edu, i) => (
                <div key={i} className="border border-border p-3 mb-2 space-y-2">
                  {[["degree","Degree"],["institution","Institution"],["location","Location"],["graduation_date","Graduation Date"]].map(([f, lbl]) => (
                    <div key={f}>
                      <label className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider flex items-center">
                        {lbl}<PlaceholderHint value={edu[f]} />
                      </label>
                      <input className={fieldClass(edu[f] || "")} value={edu[f] || ""} onChange={(e) => setEduField(i, f, e.target.value)} />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Skills */}
            <div>
              <SectionHeader title="Skills">
                <button onClick={addSkill} className="flex items-center gap-1 text-[#0025D8] font-mono text-[9px] hover:underline">
                  <Plus className="w-3 h-3" /> Add category
                </button>
              </SectionHeader>
              {(data.skills || []).map((sk, i) => (
                <div key={i} className="flex gap-2 mb-2 items-start">
                  <div className="flex-1 space-y-1">
                    <input className={fieldClass(sk.name || "")} value={sk.name || ""} onChange={(e) => setSkillName(i, e.target.value)} placeholder="Category name" />
                    <input
                      className={`${fieldClass((sk.items || []).join(", "))} text-xs`}
                      value={(sk.items || []).join(", ")}
                      onChange={(e) => setSkillItems(i, e.target.value)}
                      placeholder="Skill 1, Skill 2, Skill 3"
                    />
                  </div>
                  <button onClick={() => removeSkill(i)} className="mt-1 text-muted-foreground hover:text-[#E11D48] flex-shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Certifications */}
            {(data.certifications || []).length > 0 && (
              <div>
                <SectionHeader title="Certifications" />
                {data.certifications.map((cert, i) => (
                  <div key={i} className="grid grid-cols-3 gap-1 mb-2">
                    {[["name","Name"],["issuer","Issuer"],["date","Date"]].map(([f, lbl]) => (
                      <div key={f}>
                        <label className="font-mono text-[9px] text-muted-foreground">{lbl}</label>
                        <input className={fieldClass(cert[f] || "")} value={cert[f] || ""} onChange={(e) => setCertField(i, f, e.target.value)} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>

        {/* ── Right: Live preview ───────────────────────────────────── */}
        <div className="col-span-3">
          <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.2em] mb-2">
            Live preview — {GUIDANCE.find(g => g.id === template)?.label}
          </div>
          <div className="border border-border overflow-auto max-h-[700px] bg-gray-50">
            <div className="transform scale-[0.85] origin-top-left" style={{ width: "117.6%" }}>
              <PreviewComponent data={data} editable onPlaceholderFill={handlePlaceholderFill} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Download bar ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between border border-border bg-white px-4 py-3">
        <div className="text-xs text-muted-foreground">
          {saveStatus === "saved" && analysisId && <span className="text-[#10B981] font-mono text-[10px]">✓ All changes saved</span>}
          {!analysisId && <span className="font-mono text-[10px] text-muted-foreground">Changes apply to this session only</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={handlePDF} disabled={downloading}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0025D8] hover:bg-[#001CB3] text-white text-xs font-mono font-bold transition-colors disabled:opacity-50">
            <Download className="w-3.5 h-3.5" />
            {downloading ? "Generating…" : "Download PDF"}
          </button>
          <button onClick={handleDocx} disabled={downloadingDocx}
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#0025D8] text-[#0025D8] hover:bg-[#0025D8]/5 text-xs font-mono font-bold transition-colors disabled:opacity-50">
            <FileText className="w-3.5 h-3.5" />
            {downloadingDocx ? "Generating…" : "Download DOCX"}
          </button>
        </div>
      </div>

      {downloadGuard && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setDownloadGuard(null)}
        >
          <div
            className="bg-white border border-border max-w-md w-full p-6 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold mb-1">Unfilled placeholders</div>
                <p className="text-sm text-muted-foreground">
                  Click the amber-highlighted values in the preview on the right to fill them in.
                </p>
              </div>
              <button onClick={() => setDownloadGuard(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="space-y-1 mb-5 max-h-40 overflow-y-auto">
              {detectPlaceholders(data).map((p, i) => (
                <li key={i} className="text-xs font-mono bg-amber-50 border border-amber-200 px-2 py-1 flex gap-2">
                  <span className="text-amber-700 font-bold flex-shrink-0">{p.placeholder}</span>
                  <span className="text-muted-foreground truncate">{p.text.slice(0, 60)}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDownloadGuard(null)}
                className="px-4 py-2 text-sm font-mono border border-border hover:bg-secondary/50"
              >
                Go back
              </button>
              <button
                onClick={() => { setDownloadGuard(null); downloadGuard === "pdf" ? doDownloadPDF() : doDownloadDocx(); }}
                className="px-4 py-2 text-sm font-mono bg-[#0025D8] text-white hover:bg-[#001CB3]"
              >
                Download anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
